import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useFirebase } from './contexts/FirebaseContext';
import Sidebar from './components/Sidebar';
import BurgerIcon from './components/BurgerIcon';
import Home from './pages/Home';
import Playlists from './pages/Playlists';
import PlaylistDetail from './pages/PlaylistDetail';
import Settings from './pages/Settings';
import Stories from './pages/Stories';
import Profile from './pages/Profile';
import Chatbot from './pages/Chatbot';
import ChatbotTest from './pages/Chatbot-test';
import WelcomePage from './pages/WelcomePage';
import AccountSetup from './pages/AccountSetup';
import OAuthSuccess from './pages/OAuthSuccess';
import FeatureRequests from './pages/FeatureRequests';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import { PlaylistProvider } from './context/PlaylistContext';
import { WordProvider } from './context/WordContext';
import { WordCountProvider } from './context/WordCountContext';
import parisFinal from './images/paris-final1.png';
import kyotoFinal from './images/kyoto5.jpg';
import veniceImage from './images/venice-final.png';
import spainImage from './images/parkguell-final1.png';
import latinAmericaImage from './images/southamerica-final.png';
import koreaImage from './images/korea-final1.png';
import greatwallFinal from './images/greatwall-final.png';
import apiClient from './utils/apiClient';
import logger from './utils/logger';
import setupConsoleDebug from './utils/setupConsoleDebug';

// Make the environment check more robust
// Initialize console debug commands (only in development)
const isDevMode = typeof window !== 'undefined' && 
                 window.process && 
                 window.process.env && 
                 window.process.env.NODE_ENV === 'development';

if (isDevMode) {
  setupConsoleDebug();
}

// Add loading timeout for stuck screens
function useLoadingTimeout(loading, timeoutMs = 15000) {
    const [timedOut, setTimedOut] = useState(false);
    
    useEffect(() => {
        let timeoutId;
        
        if (loading) {
            // If loading starts, set a timeout
            timeoutId = setTimeout(() => {
                logger.warn(`App - Loading timed out after ${timeoutMs}ms`);
                setTimedOut(true);
            }, timeoutMs);
        } else {
            // If loading ends, clear any existing timeout
            setTimedOut(false);
        }
        
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [loading, timeoutMs]);
    
    return timedOut;
}

// Route guard component to check if user has completed account setup
const UserSetupCheck = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useFirebase();
    const [loading, setLoading] = useState(true);
    const [setupComplete, setSetupComplete] = useState(true);
    
    // Add timeout for the setup check to prevent hanging
    const checkTimedOut = useLoadingTimeout(loading, 8000);
    
    useEffect(() => {
        const checkUserSetup = async () => {
            try {
                // Skip check if we're already on the account setup page
                if (location.pathname === '/account-setup') {
                    logger.debug('UserSetupCheck - Already on account setup page, skipping check');
                    setLoading(false);
                    return;
                }
                
                // Check for the needsAccountSetup flag first (set by FirebaseAuthModal)
                const needsSetup = localStorage.getItem('needsAccountSetup');
                if (needsSetup === 'true') {
                    logger.info('UserSetupCheck - Found needsAccountSetup flag in localStorage, redirecting to setup');
                    setSetupComplete(false);
                    setLoading(false);
                    return;
                }
                
                // Do the check only when user is authenticated
                if (!user || !isAuthenticated) {
                    logger.debug('UserSetupCheck - User not authenticated, skipping check');
                    setLoading(false);
                    return;
                }
                
                logger.debug('UserSetupCheck - Checking if user needs setup', user.uid);
                
                // Add timeout to the API call
                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort();
                }, 5000); // 5 second timeout
                
                try {
                    // Check if user is new (no username in profile)
                    const response = await apiClient.get('/api/profile', {
                        signal: controller.signal
                    });
                    
                    clearTimeout(timeoutId);
                    
                    logger.debug(`UserSetupCheck - Profile API response status: ${response.status}`);
                    
                    if (response.status === 200) {
                        // User exists and has profile
                        const userData = await response.json();
                        logger.debug('UserSetupCheck - User data:', userData);
                        
                        if (!userData.username || userData.needsSetup === true) {
                            // User exists but has no username, setup not complete
                            setSetupComplete(false);
                            localStorage.setItem('needsAccountSetup', 'true'); // Set flag for consistency
                            logger.info('UserSetupCheck - User needs to complete setup (no username or needsSetup=true)');
                        } else {
                            setSetupComplete(true);
                            localStorage.removeItem('needsAccountSetup'); // Clear flag just in case
                            logger.debug('UserSetupCheck - User setup is complete');
                        }
                    } else if (response.status === 401 || response.status === 404) {
                        // User doesn't exist or not authenticated
                        setSetupComplete(false);
                        localStorage.setItem('needsAccountSetup', 'true'); // Set flag for consistency
                        logger.info('UserSetupCheck - User is new or not found in database, needs setup');
                    }
                } catch (fetchError) {
                    clearTimeout(timeoutId);
                    
                    if (fetchError.name === 'AbortError') {
                        logger.warn('UserSetupCheck - API call timed out, assuming setup is complete to avoid blocking user');
                        setSetupComplete(true);
                    } else {
                        throw fetchError; // Re-throw to be caught by outer try/catch
                    }
                }
            } catch (error) {
                logger.error('UserSetupCheck - Error checking user setup:', error);
                // On error, we should give the benefit of the doubt rather than trap the user in setup
                setSetupComplete(true);
                // Only set needsAccountSetup in very specific cases where we're sure
                if (error.message && error.message.includes('not found') && user && isAuthenticated) {
                    localStorage.setItem('needsAccountSetup', 'true');
                }
            } finally {
                setLoading(false);
            }
        };
        
        checkUserSetup();
    }, [user, isAuthenticated, location.pathname, navigate]);
    
    if (loading && !checkTimedOut) {
        return <div className="loading-container">Loading...</div>;
    }
    
    // If check timed out, assume setup is complete to prevent blocking the user
    if (checkTimedOut) {
        logger.warn('UserSetupCheck - Setup check timed out, allowing user to proceed');
        return children;
    }
    
    if (!setupComplete && location.pathname !== '/account-setup') {
        logger.info('UserSetupCheck - Redirecting to account setup');
        return <Navigate to="/account-setup" replace />;
    }
    
    return children;
};

function App() {
    const { isAuthenticated, user, loading, googleSignInProgress } = useFirebase();
    const [settings, setSettings] = useState({ 
        learningLanguage: 'Spanish',
        spanishRegion: localStorage.getItem('spanishRegion') || 'Latin America' 
    });
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        // Check if on mobile and default to closed if so
        return window.innerWidth > 768;
    });
    const [currentPath, setCurrentPath] = useState('');
    // This state is used to signal authentication from our custom modal
    const [customAuthenticated, setCustomAuthenticated] = useState(false);

    // Add loading timeout to prevent infinite loading, but extend if Google sign-in is in progress
    const loadingTimedOut = useLoadingTimeout(loading && !googleSignInProgress, 20000);

    // Check for manual token storage as a fallback
    const hasTokens = localStorage.getItem('access_token');

    useEffect(() => {
        // Add a global variable to track when settings were last fetched
        window.lastSettingsFetchTime = window.lastSettingsFetchTime || 0;
        
        const fetchSettings = async () => {
            try {
                if (!isAuthenticated && !customAuthenticated && !hasTokens) {
                    logger.debug('App - Not authenticated, skipping settings fetch');
                    return;
                }
                
                // Add a global throttle - ensure no more than one call every 2 seconds
                const now = Date.now();
                if (!window.GLOBAL_LAST_SETTINGS_THROTTLE) {
                    window.GLOBAL_LAST_SETTINGS_THROTTLE = 0;
                }
                
                if (now - window.GLOBAL_LAST_SETTINGS_THROTTLE < 2000) { // 2 seconds
                    logger.debug('App - Too many settings requests, throttling');
                    return;
                }
                // Update throttle timestamp
                window.GLOBAL_LAST_SETTINGS_THROTTLE = now;
                
                // Force fetch when explicitly told to ignore cache
                const shouldIgnoreCache = window.FORCE_SETTINGS_REFRESH === true;
                if (shouldIgnoreCache) {
                    window.FORCE_SETTINGS_REFRESH = false;
                    logger.debug('App - Force refreshing settings, ignoring cache');
                } else {
                    // Check if we've recently fetched settings (within the last 20 seconds)
                    if (window.lastSettingsFetchTime && (now - window.lastSettingsFetchTime < 20000)) {
                        logger.debug('App - Using cached settings (fetched in the last 20s)');
                        return;
                    }
                }
                
                // Track if a settings fetch is in progress to prevent duplicate calls
                if (window.settingsFetchInProgress) {
                    logger.debug('App - Settings fetch already in progress, skipping duplicate call');
                    return;
                }
                
                window.settingsFetchInProgress = true;
                logger.debug('App - Fetching settings...');
                const response = await apiClient.get('/api/settings');
                
                // Update the last fetch time
                window.lastSettingsFetchTime = Date.now();
                window.settingsFetchInProgress = false;
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch settings: ${response.status}`);
                }
                
                const data = await response.json();
                logger.debug('App - Settings fetched successfully:', data);
                
                // Update our settings state
                setSettings({
                    ...data,
                    learningLanguage: data.learningLanguage || 'Spanish',
                    spanishRegion: data.learningLanguage === 'Spanish' ? (data.spanishRegion || 'Latin America') : ''
                });
                
                // Also update localStorage
                localStorage.setItem('learningLanguage', data.learningLanguage || 'Spanish');
                localStorage.setItem('languageLevel', data.languageLevel || 'Beginner');
                localStorage.setItem('sentencesPerDay', data.sentencesPerDay || 5);
                localStorage.setItem('daysOfReview', data.daysOfReview || 3);
                localStorage.setItem('repeatPercentage', data.repeatPercentage || 0);
                localStorage.setItem('dailyStoryRepeatNumber', data.dailyStoryRepeatNumber || 0);
                localStorage.setItem('memoryEnabled', Boolean(data.memoryEnabled).toString());
                
                if (data.learningLanguage === 'Spanish') {
                    localStorage.setItem('spanishRegion', data.spanishRegion || 'Latin America');
                }
                
                // Success, we've authenticated via our custom modal
                setCustomAuthenticated(false);
                
                // Return the data for use if needed
                return data;
            } catch (error) {
                // Clear the in-progress flag in case of error
                window.settingsFetchInProgress = false;
                
                logger.error('App - Error fetching settings:', error.message, error.stack);
                // Check if the error is due to authentication failure
                if (error.message.includes('Authentication required') || error.message.includes('Session expired')) {
                    logger.warn('App - Authentication error during settings fetch, clearing tokens');
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('id_token');
                    localStorage.removeItem('refresh_token');
                    setCustomAuthenticated(false);
                }
            }
        };
        
        if (isAuthenticated || customAuthenticated || hasTokens) {
            fetchSettings();
        }
        
        // Add event listeners to refresh settings when the page regains focus
        // This helps synchronize settings between multiple tabs or browsers
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                logger.debug('App - Page became visible, refreshing settings');
                window.FORCE_SETTINGS_REFRESH = true;
                if (isAuthenticated || customAuthenticated || hasTokens) {
                    fetchSettings();
                }
            }
        };
        
        const handleFocus = () => {
            logger.debug('App - Window gained focus, refreshing settings');
            window.FORCE_SETTINGS_REFRESH = true;
            if (isAuthenticated || customAuthenticated || hasTokens) {
                fetchSettings();
            }
        };
        
        // Listen for storage events to detect settings changes in other tabs
        const handleStorageChange = (e) => {
            if (e.key === 'lastSettingsUpdate') {
                logger.debug('App - Settings updated in another tab, refreshing');
                window.FORCE_SETTINGS_REFRESH = true;
                if (isAuthenticated || customAuthenticated || hasTokens) {
                    fetchSettings();
                }
            }
        };
        
        // Listen for custom settings-updated events from Settings.js
        const handleSettingsUpdated = () => {
            logger.debug('App - Received settings-updated event');
            window.FORCE_SETTINGS_REFRESH = true;
            if (isAuthenticated || customAuthenticated || hasTokens) {
                fetchSettings();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('settings-updated', handleSettingsUpdated);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('settings-updated', handleSettingsUpdated);
        };
    }, [isAuthenticated, customAuthenticated, hasTokens]);

    useEffect(() => {
        // Create a style element for our custom CSS
        let styleElement = document.getElementById('background-styles');
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'background-styles';
            document.head.appendChild(styleElement);
        }

        if (settings.learningLanguage === 'French') {
            // Apply styles through a CSS rule instead of inline styles
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${parisFinal}");
                    background-size: cover;
                    background-position: center bottom;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (settings.learningLanguage === 'Japanese') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${kyotoFinal}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (settings.learningLanguage === 'Italian') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${veniceImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (settings.learningLanguage === 'Korean') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${koreaImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (settings.learningLanguage === 'Chinese') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${greatwallFinal}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (settings.learningLanguage === 'Spanish') {
            // For Spanish, check the region
            const spanishRegion = settings.spanishRegion || localStorage.getItem('spanishRegion') || 'Latin America';
            const backgroundImage = spanishRegion === 'Spain' ? spainImage : latinAmericaImage;
            
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${backgroundImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else {
            // Remove the ::before element when not using a background image
            styleElement.innerHTML = `
                body::before {
                    display: none;
                }
                body {
                    background-color: #f2f2f2 !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        }
    }, [settings.learningLanguage, settings.spanishRegion]);

    const handleLanguageChange = (lang) => {
        setSettings(prevSettings => ({ ...prevSettings, learningLanguage: lang }));

        // Optionally, re-fetch settings to ensure backend and frontend are in sync
        const fetchSettings = async () => {
            try {
                // Use apiClient for automatic token handling
                const response = await apiClient.get('/api/settings');
                if (response.ok) {
                    const data = await response.json();
                    setSettings({ ...data, learningLanguage: data.learningLanguage || 'Spanish' });
                    logger.debug('App - Settings re-fetched successfully after language change');
                }
            } catch (error) {
                logger.error('App - Error re-fetching settings after language change:', error);
            }
        };
        if (isAuthenticated || hasTokens) {
            fetchSettings();
        }
    };

    const handleSpanishRegionChange = (region) => {
        setSettings(prevSettings => ({ ...prevSettings, spanishRegion: region }));
        logger.debug('App - Spanish region changed to:', region);
    };

    // Show a simple loading screen
    if (loading && !loadingTimedOut) {
        // If Google sign-in is in progress, show a special Google loading screen
        if (googleSignInProgress) {
            return (
                <div style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    margin: '0', 
                    maxWidth: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#132431',
                    color: 'white',
                    fontFamily: 'Arial, sans-serif'
                }}>
                    <div style={{ marginBottom: '20px' }}>
                        <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="white" opacity="0.3"/>
                            <path d="M12 4V2C6.48 2 2 6.48 2 12H4C4 7.59 7.59 4 12 4Z" fill="white">
                                <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="1s"
                                    repeatCount="indefinite"
                                />
                            </path>
                        </svg>
                    </div>
                    <h2 style={{ color: 'white', marginBottom: '10px' }}>Completing Google Sign-in</h2>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Please wait while we authenticate your account...</p>
                </div>
            );
        }
        
        // Regular loading screen
        return (
            <div style={{ 
                padding: '30px', 
                textAlign: 'center', 
                margin: '0', 
                maxWidth: '100%',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#132431',
                color: 'white',
                fontFamily: 'Arial, sans-serif'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="white" opacity="0.3"/>
                        <path d="M12 4V2C6.48 2 2 6.48 2 12H4C4 7.59 7.59 4 12 4Z" fill="white">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 12 12"
                                to="360 12 12"
                                dur="1s"
                                repeatCount="indefinite"
                            />
                        </path>
                    </svg>
                </div>
                <h2 style={{ color: 'white', marginBottom: '10px' }}>Loading</h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Please wait while we set up your experience...</p>
            </div>
        );
    }
    
    // If loading timed out, show a helpful message with reset option
    if (loadingTimedOut) {
        return (
            <div className="loading-error-container" style={{ 
                padding: '30px', 
                textAlign: 'center', 
                margin: '0', 
                maxWidth: '100%',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#132431',
                color: 'white',
                fontFamily: 'Arial, sans-serif'
            }}>
                <h2 style={{ color: 'white', marginBottom: '20px' }}>Loading is taking longer than expected</h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '10px' }}>This could be due to network issues or a problem with your account state.</p>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '10px' }}>You can try:</p>
                <ul style={{ textAlign: 'left', display: 'inline-block', marginBottom: '30px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <li>Refreshing the page</li>
                    <li>Checking your internet connection</li>
                    <li>Clearing your browser cache and cookies</li>
                </ul>
                <div>
                    <button 
                        onClick={() => window.location.reload()} 
                        style={{ 
                            padding: '12px 24px', 
                            margin: '0 10px', 
                            backgroundColor: '#66ccff',
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            transition: 'background-color 0.3s'
                        }}
                    >
                        Refresh Page
                    </button>
                    <button 
                        onClick={() => {
                            localStorage.clear();
                            window.location.href = '/welcome';
                        }} 
                        style={{ 
                            padding: '12px 24px', 
                            margin: '0 10px', 
                            backgroundColor: 'transparent', 
                            color: 'white', 
                            border: '1px solid rgba(255, 255, 255, 0.3)', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            transition: 'border-color 0.3s, background-color 0.3s'
                        }}
                    >
                        Return to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <PlaylistProvider userId={user ? user.uid : undefined}>
            <WordProvider>
                <WordCountProvider>
                    <Router>
                        {(isAuthenticated || customAuthenticated || hasTokens) ? (
                            <Routes>
                                <Route path="/account-setup" element={<AccountSetup />} />
                                <Route path="/*" element={
                                    <UserSetupCheck>
                                        <AppContent 
                                            sidebarOpen={sidebarOpen} 
                                            setSidebarOpen={setSidebarOpen} 
                                            settings={settings} 
                                            handleLanguageChange={handleLanguageChange}
                                            handleSpanishRegionChange={handleSpanishRegionChange} 
                                        />
                                    </UserSetupCheck>
                                } />
                            </Routes>
                        ) : (
                            <Routes>
                                <Route
                                    path="/"
                                    element={
                                        <WelcomePage onAuthenticated={() => setCustomAuthenticated(true)} />
                                    }
                                />
                                <Route path="/oauth-success" element={<OAuthSuccess />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        )}
                    </Router>
                </WordCountProvider>
            </WordProvider>
        </PlaylistProvider>
    );
}

// New component to handle content and route-based conditionals
const AppContent = ({ sidebarOpen, setSidebarOpen, settings, handleLanguageChange, handleSpanishRegionChange }) => {
    const location = useLocation();
    const isPricingPage = location.pathname === '/pricing';
    const isPolicyPage = location.pathname === '/privacy-policy' || location.pathname === '/terms-of-service';
    
    return (
        <>
            {!isPricingPage && !isPolicyPage && <BurgerIcon toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />}
            {sidebarOpen && !isPricingPage && !isPolicyPage && <Sidebar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />}
            <div className={`main-content ${!isPricingPage && !isPolicyPage && sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                <div className="content-wrapper">
                    <Routes>
                        <Route path="/" element={<Home settings={settings} refreshSettings={settings.learningLanguage} />} />
                        <Route path="/playlists" element={<Playlists />} />
                        <Route path="/playlists/:playlistId" element={<PlaylistDetail />} />
                        <Route path="/settings" element={<Settings onLanguageChange={handleLanguageChange} onSpanishRegionChange={handleSpanishRegionChange} />} />
                        <Route path="/stories" element={<Stories />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/chatbot" element={<Chatbot />} />
                        <Route path="/chatbot-test" element={<ChatbotTest />} />
                        <Route path="/feature-requests" element={<FeatureRequests />} />
                        <Route path="/contact-us" element={<Contact />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
            </div>
        </>
    );
};

export default App;